import { WifiOff, RefreshCw, Home, LogIn } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DiagnosticLogs } from '@/components/common/DiagnosticLogs';
import { logger } from '@/lib/logger';
import { signOutSession } from '@/lib/sign-out';

interface NetworkErrorPageProps {
  error?: string;
  isAuthError?: boolean;
  onRetry?: () => void;
}

export const NetworkErrorPage: React.FC<NetworkErrorPageProps> = ({
  error = 'Unable to connect to the server',
  isAuthError = false,
  onRetry,
}) => {
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOutSession();
      window.location.href = '/member/login';
    } catch (error) {
      logger.error('Error signing out:', error);
      window.location.href = '/member/login';
    }
  };

  const handleRefresh = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[hsl(var(--background))]">
      <div className="max-w-lg w-full bg-[hsl(var(--card))] rounded-2xl shadow-[var(--shadow-xl)] border border-[hsl(var(--border))]">
        {/* Header */}
        <div className="text-center px-8 pt-10 pb-6">
          <div className="mx-auto mb-5 w-16 h-16 bg-[hsl(var(--error)/0.08)] rounded-full flex items-center justify-center">
            <WifiOff className="h-7 w-7 text-[hsl(var(--error))]" />
          </div>
          <h1 className="text-2xl font-semibold text-[hsl(var(--foreground))] tracking-tight">
            {isAuthError ? 'Authentication Error' : 'Connection Error'}
          </h1>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
            {isAuthError ? 'Your session has expired or you need to sign in again.' : error}
          </p>
        </div>

        {/* Content */}
        <div className="px-8 pb-8 space-y-5">
          {/* Help tips */}
          <div className="bg-[hsl(var(--secondary))] p-4 rounded-xl space-y-2">
            <p className="text-sm font-medium text-[hsl(var(--foreground))]">What you can try:</p>
            <ul className="text-sm text-[hsl(var(--muted-foreground))] space-y-1 list-disc list-inside">
              {!isAuthError && (
                <>
                  <li>Check if your backend server is running</li>
                  <li>Verify your internet connection</li>
                  <li>Check if you're connected to VPN (if required)</li>
                </>
              )}
              <li>Clear your browser cache and cookies</li>
              <li>Try using a different browser or incognito mode</li>
            </ul>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2.5">
            {isAuthError ? (
              <>
                <button
                  onClick={handleSignOut}
                  className="flex items-center justify-center gap-2 w-full h-11 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-full transition-colors"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In Again
                </button>
                <button
                  onClick={handleRefresh}
                  className="flex items-center justify-center gap-2 w-full h-11 text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-full transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleRefresh}
                  className="flex items-center justify-center gap-2 w-full h-11 text-sm font-semibold text-white bg-gray-900 hover:bg-gray-800 rounded-full transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retry Connection
                </button>
                <button
                  onClick={handleGoHome}
                  className="flex items-center justify-center gap-2 w-full h-11 text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-full transition-colors"
                >
                  <Home className="h-4 w-4" />
                  Go to Home
                </button>
                <button
                  onClick={handleSignOut}
                  className="flex items-center justify-center gap-2 w-full h-11 text-sm font-semibold text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-full transition-colors"
                >
                  <LogIn className="h-4 w-4" />
                  Sign In
                </button>
              </>
            )}
          </div>

          {/* Diagnostic logs */}
          <DiagnosticLogs errorContext={isAuthError ? 'Authentication Error' : error} />
        </div>
      </div>
    </div>
  );
};

export default NetworkErrorPage;
